import { formatInTimeZone } from "date-fns-tz";
import { WeatherHistoryObservation, WeatherHistoryResponse } from "../api/types/Weather";

// History on Demand API date format: yyyyMMdd'T'HHmmss'Z', UTC.
export const formatHodDateTime = (epochMs: number): string =>
  formatInTimeZone(new Date(epochMs), "UTC", "yyyyMMdd'T'HHmmss") + "Z";

// Unpacks the parallel-array HoD response into one object per observation.
export const unpackWeatherHistory = (
  data: WeatherHistoryResponse | undefined
): WeatherHistoryObservation[] => {
  if (!data?.validTimeUtc?.length) return [];
  return data.validTimeUtc.map((validTimeUtc, i) => ({
    validTimeUtc,
    epochMs: new Date(validTimeUtc).getTime(),
    temperature: data.temperature?.[i],
    temperatureFeelsLike: data.temperatureFeelsLike?.[i],
    windSpeed: data.windSpeed?.[i],
    windDirection: data.windDirection?.[i],
    relativeHumidity: data.relativeHumidity?.[i],
    visibility: data.visibility?.[i],
    pressureMeanSeaLevel: data.pressureMeanSeaLevel?.[i],
    pressureChange: data.pressureChange?.[i],
    uvIndex: data.uvIndex?.[i],
    precip1Hour: data.precip1Hour?.[i],
    iconCode: data.iconCode?.[i],
  }));
};

// Picks the observation with validTimeUtc closest to targetEpochMs.
export const closestWeatherObservation = (
  observations: WeatherHistoryObservation[],
  targetEpochMs: number
): WeatherHistoryObservation | null => {
  if (!observations.length) return null;
  return observations.reduce((closest, current) =>
    Math.abs(current.epochMs - targetEpochMs) < Math.abs(closest.epochMs - targetEpochMs)
      ? current
      : closest
  );
};

export type WeatherTimelineSlot = {
  targetEpochMs: number;
  observation: WeatherHistoryObservation;
};

// 4 timeline columns at 10-minute intervals leading up to the incident.
export const buildTenMinuteDisplayTimeline = (
  observations: WeatherHistoryObservation[],
  incidentEpochMs: number
): WeatherTimelineSlot[] => {
  if (!observations.length) return [];
  const offsetsMs = [-180, -120, -60, 0].map((min) => min * 60 * 1000);
  return offsetsMs
    .map((offsetMs) => incidentEpochMs + offsetMs)
    .map((targetEpochMs) => {
      const observation = closestWeatherObservation(observations, targetEpochMs);
      return observation ? { targetEpochMs, observation } : null;
    })
    .filter((slot): slot is WeatherTimelineSlot => slot !== null);
};

const COMPASS_POINTS = [
  "N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE",
  "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW",
];

// 16-point compass label for a wind direction in degrees (0-360).
export const windDegreesToCompass = (degrees: number): string => {
  if (degrees === undefined || degrees === null || isNaN(degrees)) return "-";
  const index = Math.round(((degrees % 360) + 360) % 360 / 22.5) % 16;
  return COMPASS_POINTS[index];
};

// Rising/Falling/Steady label from a 3-hour pressure change value (hPa).
export const pressureTrendLabel = (pressureChange: number): string => {
  if (pressureChange === undefined || pressureChange === null || isNaN(pressureChange)) return "-";
  if (pressureChange > 0) return "Rising";
  if (pressureChange < 0) return "Falling";
  return "Steady";
};

// NWS/Rothfusz heat index regression (operates in F, returns C)
export const calculateHeatIndexCelsius = (tempC: number, relativeHumidity: number): number => {
  const tempF = (tempC * 9) / 5 + 32;
  if (tempF < 80 || relativeHumidity === undefined || relativeHumidity === null) return tempC;

  const T = tempF;
  const R = relativeHumidity;
  const heatIndexF =
    -42.379 +
    2.04901523 * T +
    10.14333127 * R -
    0.22475541 * T * R -
    0.00683783 * T * T -
    0.05481717 * R * R +
    0.00122874 * T * T * R +
    0.00085282 * T * R * R -
    0.00000199 * T * T * R * R;

  return ((heatIndexF - 32) * 5) / 9;
};
